'use client'

import React from 'react'
import { useState } from 'react'

import { useKeenSlider, KeenSliderOptions } from 'keen-slider/react'
import KeenSliderDots from '@/components/keen-slider/KeenSliderDots'

export type CustomKeenSliderOptions = KeenSliderOptions & {
  autoplay?: number | false // Add the custom `autoplay` property.
}

import { KeenSliderFade, KeenSliderAutoplay } from '@/components/keen-slider/KeenSliderPlugins'
import 'keen-slider/keen-slider.min.css'

import HeroSubDetails from '@/components/hero/HeroSubDetails'

import type { HeroSlides, HeroSlide, HeroBrandedProps } from '@/components/hero/types'

import { HeroBrandedInner } from '@/components/hero/HeroBranded'
import { HeroVideo } from './HeroVideo'
import HeroTextSlot, { HeroTextSlotProps } from './HeroTextSlot'

export default function HeroSlider({ slides }: { slides: HeroSlides }) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const componentRef = React.useRef<HTMLDivElement>(null)

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement, CustomKeenSliderOptions>(
    {
      initial: 0,
      disabled: slides.length === 1,
      renderMode: 'custom',
      defaultAnimation: {
        duration: 1000,
      },
      loop: true,
      autoplay: slides.length > 1 ? 6000 : false,
      slides: {
        perView: 1,
        spacing: 0,
      },
      created(s) {
        setIsLoaded(true)
      },
      animationStarted(s) {
        const next = s.animator.targetIdx || 0
        const nextIndex = s.track.absToRel(next)
        setActiveSlide(nextIndex)

        const navEl = componentRef.current?.querySelector('.hero--slider-nav')
        const navWidth = navEl?.clientWidth || 0

        const target = navEl?.querySelectorAll('.hero--slider-nav button')[
          nextIndex
        ] as HTMLElement | null

        if (target) {
          const scrollLeft = target.offsetLeft - navWidth / 2 + target.clientWidth / 2

          navEl.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          })
        }
      },
    },
    [KeenSliderFade, KeenSliderAutoplay],
  )

  if (!slides) {
    return null
  }

  const multipleSlides = slides.length > 1

  return (
    <section
      className="hero--branded hero hero-padding bg-theme text-fg theme--base-0"
      ref={componentRef}>
      <div className="relative">
        <div
          ref={sliderRef}
          className={`keen-slider keen-slider--fade z-0 translate-x-[0px] overflow-hidden ${!multipleSlides ? 'ms-up:rounded-lg' : 'ms-up:rounded-t-lg'}`}>
          {slides.map((slide: HeroSlide, i: number) => {
            const {
              hero_style = 'default',
              hero_video: heroVideo,
              hero_poster: heroPoster,

              hero_brand_colour,
              hero_mode = 'dark',
              hero_brand_illustration,
              hero_brand_static_illustration,
              hero_brand_asset,
              hero_label,
              hero_title,
              hero_text,
              hero_button,
              related_post = null,
              related_post_data = null,
            } = slide

            const eventDates = slide?.related_post_data?.eventDates
            const timezone = slide?.related_post_data?.timezone
            const timezoneModal = slide?.related_post_data?.timezoneModal
            const location = slide.related_post_data?.location

            const subDetails = (
              <HeroSubDetails
                {...related_post_data}
                button={hero_button}
                postActions={false}
                eventDates={eventDates}
                timezone={timezone}
                timezoneModal={timezoneModal}
                location={location}
              />
            )

            const heroTextProps: HeroTextSlotProps = {
              heroStyle: 'branded',
              breadcrumbs: hero_label,
              title: hero_title.text,
              titleTag: hero_title.tag,
              titleClass: hero_title.class,
              text: hero_text,
              subDetails: subDetails || null,
            }

            const heroBrandedProps: HeroBrandedProps = {
              brandColor: hero_brand_colour,
              gradientType: hero_mode,
              textSlot: <HeroTextSlot {...heroTextProps} />,
              animationName: hero_brand_illustration,
              staticIllustration: hero_brand_static_illustration,
              showAsset: hero_brand_asset,
            }

            const isActive = activeSlide === i

            return (
              <div
                key={i}
                className={`keen-slider__slide ${i > 0 && !isLoaded ? 'hidden' : ''}`}
                data-index={i}
                aria-hidden={!isActive ? 'true' : 'false'}>
                {hero_style === 'default' ? (
                  <HeroBrandedInner
                    {...heroBrandedProps}
                    isActive={isActive}
                    imgLoading={i === 0 ? 'eager' : 'lazy'}
                  />
                ) : (
                  <HeroVideo
                    textSlot={<HeroTextSlot {...heroTextProps} />}
                    multipleSlides={multipleSlides}
                    video={heroVideo}
                    poster={heroPoster}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Dots */}
        {slides.length > 1 ? (
          <div className="absolute bottom-[40px] left-1/2 z-10 max-w-full translate-x-[-50%] px-l text-white ms:bottom-[24px] s:bottom-[20px]">
            <KeenSliderDots sliderInstance={instanceRef} activeSlide={activeSlide} />
          </div>
        ) : null}
      </div>

      {/* Navigation */}
      {slides.length > 1 ? (
        <HeroSliderNavigation
          sliderInstance={instanceRef}
          slides={slides}
          isLoaded={isLoaded}
          activeSlide={activeSlide}
        />
      ) : null}
    </section>
  )
}

export function HeroSliderNavigation({
  sliderInstance,
  slides,
  isLoaded,
  activeSlide,
}: {
  sliderInstance: any
  slides: HeroSlides
  isLoaded: boolean
  activeSlide: number
}) {
  if (!isLoaded) {
    return null
  }

  return (
    <section className="hero--slider-nav no-scrollbar relative flex w-full overflow-x-auto border s:hidden ms-up:rounded-b-lg">
      {slides.map((slide: HeroSlide, index: number) => {
        const title = slide.hero_slider_button_title || slide.hero_title.text
        const heading = slide?.hero_label

        const isActive = activeSlide === index
        const buttonClass = isActive ? `bg-primary-50` : ''

        return (
          <button
            key={index}
            data-index={index}
            onClick={(e) => {
              sliderInstance.current?.moveToIdx(index)
            }}
            className={`${buttonClass} flex w-full min-w-[max(30%,clamp(15rem,9.767rem+20.93vw,26.25rem))] flex-grow flex-col items-start gap-0 border-r px-m py-s text-left last:border-r-0 hover:bg-primary-50/50 xl:px-s xl:py-xs`}>
            <div className="w-full overflow-hidden">
              <span className="h6 mb-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {title}
              </span>
            </div>
            <p className="mb-0 text-[length:--p-xs-size]">{heading}</p>
          </button>
        )
      })}
    </section>
  )
}
